import type { LucideIcon } from "lucide-react";
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Boxes,
  Check,
  CheckCircle2,
  CircleAlert,
  ClipboardCheck,
  FileText,
  Globe2,
  Languages,
  LayoutDashboard,
  MapPinned,
  MessageCircleMore,
  MousePointerClick,
  PackageCheck,
  PenTool,
  QrCode,
  SearchCheck,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Store,
  Target,
  Users,
  WalletCards,
  WandSparkles,
  Zap,
} from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import { Brand } from "../components/ui/Brand";
import { getPlanBySlug, type PlanSlug } from "../data/plans";
import { freeBuildTemplate, getWhatsAppUrl } from "../lib/links";

type PlanDetailPageProps = { slug: PlanSlug };

type DetailCard = {
  icon: LucideIcon;
  title: string;
  text: string;
};

type PlanStory = {
  image: string;
  alt: string;
  visualLabel: string;
  promiseTitle: string;
  promiseText: string;
  outcomes: Array<{ value: string; label: string; text: string }>;
  dailyTitle: string;
  dailyText: string;
  dailyCards: DetailCard[];
  featureGroups: Array<{ title: string; text: string; items: string[] }>;
  faq: Array<{ question: string; answer: string }>;
};

const planStories: Record<PlanSlug, PlanStory> = {
  "siap-digital": {
    image: "/images/plans/siap-digital.webp",
    alt: "Pelaku UMKM membuat website dari percakapan WhatsApp",
    visualLabel: "Mulai dari WhatsApp, tanpa belajar dashboard",
    promiseTitle: "Cara termudah punya etalase jualan sendiri.",
    promiseText: "Siap Digital cocok untuk usaha yang baru mulai online. Semua informasi penting diringkas menjadi satu halaman yang mudah dibagikan dan langsung mengarah ke WhatsApp pemilik usaha.",
    outcomes: [
      { value: "Rp0", label: "Biaya bulanan", text: "Mulai tanpa langganan dan tanpa kartu kredit." },
      { value: "1 chat", label: "Untuk memulai", text: "Kirim data usaha secara bertahap melalui WhatsApp." },
      { value: "24/7", label: "Website aktif", text: "Link dapat dibagikan kapan saja kepada calon pembeli." },
    ],
    dailyTitle: "Jualan sederhana, tanpa alur yang membingungkan.",
    dailyText: "Paket ini fokus pada kebutuhan paling penting: tampil meyakinkan, menerima pertanyaan, dan mengarahkan pembayaran langsung ke UMKM.",
    dailyCards: [
      { icon: MessageCircleMore, title: "Buat dan ubah lewat chat", text: "Ketik #CSJW untuk memulai. Harga, foto, dan QRIS dapat diperbarui dengan perintah singkat." },
      { icon: Store, title: "Punya link jualan sendiri", text: "Website menampilkan nama usaha, produk, harga, foto, manfaat, dan tombol pemesanan." },
      { icon: QrCode, title: "Pembayaran langsung ke UMKM", text: "Gunakan QRIS milik sendiri. Konfirmasi ongkir dan total akhir dilakukan melalui WhatsApp." },
    ],
    featureGroups: [
      { title: "Tampilan jualan", text: "Informasi inti yang membuat pembeli cepat memahami produkmu.", items: ["Satu halaman website", "Satu produk utama", "Foto dan deskripsi produk", "Tombol pesan ke WhatsApp"] },
      { title: "Kontrol dari chat", text: "Perubahan ringan tidak perlu membuka panel yang rumit.", items: ["Ubah harga", "Ganti foto", "Ganti QRIS", "Cek status website"] },
      { title: "Transaksi sederhana", text: "CSJW menghubungkan pembeli langsung dengan pemilik usaha.", items: ["Subtotal di browser", "Alamat dikirim ke WhatsApp", "Ongkir dikonfirmasi UMKM", "Pembayaran QRIS statis"] },
    ],
    faq: [
      { question: "Apakah benar-benar gratis?", answer: "Ya, paket Siap Digital tidak memiliki biaya bulanan. Batasnya adalah satu website dengan satu produk utama dan pengelolaan melalui WhatsApp." },
      { question: "Apakah pesanan disimpan di dashboard?", answer: "Tidak. Pada paket ini, detail pesanan dikirim langsung ke WhatsApp UMKM agar alurnya tetap sederhana." },
      { question: "Bisakah harga dan foto diganti?", answer: "Bisa. Gunakan perintah chat seperti #HARGA, #FOTO, dan #QRIS dari nomor WhatsApp yang terdaftar." },
    ],
  },
  "siap-profesional": {
    image: "/images/plans/siap-profesional.webp",
    alt: "Pemilik usaha mengelola website dari dashboard CSJW",
    visualLabel: "Website, katalog, pesanan, dan laporan dalam satu tempat",
    promiseTitle: "Saat usaha tumbuh, pengelolaannya ikut naik kelas.",
    promiseText: "Siap Profesional memberi ruang lebih besar untuk produk, tampilan, pesanan, dan data usaha. WhatsApp tetap menjadi pintu masuk yang mudah, sementara dashboard menjadi pusat kendali harian.",
    outcomes: [
      { value: "Banyak", label: "Produk & kategori", text: "Bangun katalog yang dapat bertambah mengikuti usaha." },
      { value: "1 dashboard", label: "Pusat kendali", text: "Kelola website, konten, pesanan, dan laporan." },
      { value: "Custom", label: "Domain & tampilan", text: "Gunakan identitas brand yang lebih kuat dan profesional." },
    ],
    dailyTitle: "Semua yang dibutuhkan untuk mengelola website jualan.",
    dailyText: "Tidak hanya tampil online. Paket ini membantu bisnis mengatur katalog, menyusun halaman, membaca performa, dan menindaklanjuti pesanan.",
    dailyCards: [
      { icon: Boxes, title: "Kelola banyak produk", text: "Atur nama, harga, foto, stok, kategori, dan status produk dari dashboard." },
      { icon: PenTool, title: "Susun halaman secara visual", text: "Pilih template, tambah section, ubah urutan, dan lihat preview sebelum dipublikasikan." },
      { icon: BarChart3, title: "Pahami aktivitas pembeli", text: "Pantau kunjungan, klik WhatsApp, pesanan, dan produk yang paling menarik perhatian." },
    ],
    featureGroups: [
      { title: "Katalog & transaksi", text: "Pengalaman belanja yang lebih lengkap untuk pembeli.", items: ["Banyak produk", "Kategori produk", "Keranjang belanja", "Checkout dan riwayat order"] },
      { title: "Builder visual", text: "Buat halaman yang sesuai karakter brand tanpa menyentuh kode.", items: ["Template siap pilih", "Drag-and-drop section", "Preview desktop dan mobile", "Simpan draft dan publish"] },
      { title: "Kontrol bisnis", text: "Data penting tersusun agar lebih mudah ditindaklanjuti.", items: ["Dashboard ringkas", "Analitik dasar", "Pengelolaan pesanan", "Custom domain"] },
    ],
    faq: [
      { question: "Apakah saya masih bisa memakai WhatsApp?", answer: "Bisa. WhatsApp digunakan untuk memulai dan melakukan pembaruan cepat, sedangkan dashboard dipakai untuk pengelolaan yang lebih lengkap." },
      { question: "Apakah builder harus digunakan?", answer: "Tidak wajib. Kamu dapat memakai template yang sudah siap, lalu mengubah bagian tertentu ketika dibutuhkan." },
      { question: "Kapan fitur paket aktif?", answer: "Nomor WhatsApp dan akun diaktifkan setelah pembayaran terverifikasi. Setelah itu, #CSJW dan dashboard menggunakan data usaha yang sama." },
    ],
  },
  "siap-export": {
    image: "/images/plans/siap-export.webp",
    alt: "UMKM menyiapkan produk dan profil untuk pasar ekspor",
    visualLabel: "Profil global, market intelligence, dan buyer workspace",
    promiseTitle: "Bukan sekadar tampil global. Bersiaplah bicara dengan buyer.",
    promiseText: "Siap Export melengkapi website Profesional dengan informasi yang biasa dicari buyer: profil dua bahasa, kapasitas, MOQ, lead time, kemasan, kesiapan dokumen, peluang negara, dan tindak lanjut inquiry.",
    outcomes: [
      { value: "2 bahasa", label: "Profil bisnis", text: "Sajikan usaha dalam Bahasa Indonesia dan Inggris." },
      { value: "Peta", label: "Peluang pasar", text: "Lihat prioritas negara berdasarkan kecocokan indikatif produk." },
      { value: "B2B", label: "Buyer workflow", text: "Kelola inquiry, pitch, quotation, dan tindak lanjut." },
    ],
    dailyTitle: "Perangkat kerja untuk menyiapkan peluang ekspor.",
    dailyText: "Informasi ekspor dibuat lebih terstruktur agar UMKM dapat mengetahui apa yang sudah siap, apa yang masih kurang, dan bagaimana berbicara dengan calon buyer.",
    dailyCards: [
      { icon: MapPinned, title: "Temukan pasar prioritas", text: "Peta interaktif menampilkan opportunity score, tingkat persaingan, kebutuhan awal, dan profil buyer per negara." },
      { icon: ClipboardCheck, title: "Ukur kesiapan ekspor", text: "Checklist membantu menilai profil, spesifikasi, kapasitas, harga, dokumen, dan kepatuhan." },
      { icon: Users, title: "Kelola buyer dan outreach", text: "Susun buyer workspace, email pitch berbahasa Inggris, inquiry, quotation, dan langkah tindak lanjut." },
    ],
    featureGroups: [
      { title: "Profil ekspor", text: "Informasi produk yang lebih siap dibaca calon buyer.", items: ["Website bilingual", "MOQ dan kapasitas", "Lead time dan kemasan", "Sertifikasi dan shelf life"] },
      { title: "Market intelligence", text: "Bantu memilih negara yang layak dipelajari lebih dahulu.", items: ["Peta pasar interaktif", "Opportunity score", "Data makro negara", "Market brief dan entry notes"] },
      { title: "Buyer tools", text: "Siapkan komunikasi B2B dengan lebih rapi.", items: ["Buyer workspace", "Pitch copilot", "Landed-cost simulator", "Inquiry dan quotation workflow"] },
    ],
    faq: [
      { question: "Apakah CSJW menjamin produk pasti diekspor?", answer: "Tidak. Fitur ini membantu persiapan, analisis indikatif, dan komunikasi buyer. Keputusan transaksi tetap bergantung pada produk, kepatuhan, negosiasi, dan kebutuhan pasar." },
      { question: "Apakah opportunity score merupakan data resmi perdagangan?", answer: "Opportunity score adalah model indikatif untuk membantu prioritas awal. Data makro dapat menggunakan sumber publik, tetapi keputusan bisnis tetap perlu divalidasi lebih lanjut." },
      { question: "Apakah profil ekspor bisa diperbarui?", answer: "Bisa. MOQ, kapasitas, lead time, kemasan, sertifikasi, bahasa, dan target pasar dapat diperbarui melalui dashboard." },
    ],
  },
};

const planAccent: Record<PlanSlug, { badge: string; button: string; panel: string; icon: string }> = {
  "siap-digital": {
    badge: "border-brand-200 bg-brand-50 text-brand-700",
    button: "bg-brand-600 hover:bg-brand-700 shadow-brand-600/20",
    panel: "from-brand-700 via-brand-800 to-emerald-950",
    icon: "bg-brand-50 text-brand-700",
  },
  "siap-profesional": {
    badge: "border-sky-200 bg-sky-50 text-sky-700",
    button: "bg-sky-600 hover:bg-sky-700 shadow-sky-600/20",
    panel: "from-slate-950 via-sky-950 to-brand-900",
    icon: "bg-sky-50 text-sky-700",
  },
  "siap-export": {
    badge: "border-accent-200 bg-accent-50 text-accent-700",
    button: "bg-accent-500 hover:bg-accent-600 shadow-accent-500/20",
    panel: "from-emerald-950 via-brand-900 to-accent-950",
    icon: "bg-accent-50 text-accent-700",
  },
};

const featureIconFallbacks: LucideIcon[] = [LayoutDashboard, ShoppingCart, WandSparkles, Globe2, WalletCards, SearchCheck];

export function PlanDetailPage({ slug }: PlanDetailPageProps) {
  const plan = getPlanBySlug(slug);
  if (!plan) return <Navigate to="/" replace />;

  const story = planStories[slug];
  const accent = planAccent[slug];
  const isFree = plan.code === "FREE";
  const cta = isFree ? getWhatsAppUrl(freeBuildTemplate) : `/checkout/${plan.slug}`;

  return (
    <main className="min-h-screen bg-[#fbfcf8] text-slate-950">
      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/[.88] backdrop-blur-2xl">
        <div className="app-container flex items-center justify-between gap-4 py-3.5">
          <Brand />
          <div className="flex items-center gap-2">
            <Link to="/" className="btn-secondary !rounded-xl !px-4 !py-2.5"><ArrowLeft size={16} /> Kembali</Link>
            <Link to="/login" className="hidden text-sm font-extrabold text-slate-500 hover:text-brand-700 sm:block">Masuk dashboard</Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden pb-16 pt-14 sm:pb-24 sm:pt-20">
        <div className="hero-orb hero-orb--green" />
        <div className="hero-orb hero-orb--orange" />
        <div className="soft-grid absolute inset-0 opacity-40" />
        <div className="app-container relative grid items-center gap-12 lg:grid-cols-[.94fr_1.06fr]">
          <div>
            <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-black ${accent.badge}`}><Sparkles size={14} /> {plan.eyebrow}</span>
            <p className="mt-6 text-xs font-black uppercase tracking-[.22em] text-accent-600">{plan.name}</p>
            <h1 className="mt-4 max-w-3xl text-5xl font-black leading-[.95] tracking-[-.065em] text-slate-950 sm:text-6xl">{plan.hero}</h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600">{plan.supportingText}</p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              {isFree ? (
                <a href={cta} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm font-black text-white shadow-lg transition hover:-translate-y-0.5 ${accent.button}`}>
                  <MessageCircleMore size={18} /> {plan.primaryCta}<ArrowRight size={17} />
                </a>
              ) : (
                <Link to={cta} className={`inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm font-black text-white shadow-lg transition hover:-translate-y-0.5 ${accent.button}`}>
                  {plan.primaryCta}<ArrowRight size={17} />
                </Link>
              )}
              <a href="#fitur" className="btn-secondary !px-6 !py-3.5">Jelajahi fiturnya</a>
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {plan.audience.map((item) => <span key={item} className="rounded-full border border-slate-200 bg-white/80 px-3 py-2 text-xs font-bold text-slate-600 shadow-sm">{item}</span>)}
            </div>
          </div>

          <div className="relative min-h-[560px]">
            <div className="absolute inset-0 overflow-hidden rounded-[2.5rem] border border-white bg-white p-2 shadow-[0_42px_110px_rgba(20,55,32,.20)]">
              <img src={story.image} alt={story.alt} className="h-full w-full rounded-[2.1rem] bg-[#eef6ef] object-contain" />
              <div className="absolute inset-2 rounded-[2.1rem] bg-gradient-to-t from-emerald-950/[.38] via-transparent to-transparent" />
            </div>
            <div className="absolute bottom-5 left-5 right-5 rounded-[1.6rem] border border-white/40 bg-white/[.91] p-5 shadow-2xl backdrop-blur-xl sm:left-auto sm:right-7 sm:w-[380px]">
              <p className="text-xs font-black uppercase tracking-[.17em] text-brand-700">{story.visualLabel}</p>
              <div className="mt-4 flex items-end gap-2"><span className="text-4xl font-black tracking-[-.06em] text-slate-950">{plan.priceLabel}</span><span className="pb-1 text-xs font-semibold text-slate-400">{plan.suffix}</span></div>
              <p className="mt-3 text-sm leading-6 text-slate-500">{plan.description}</p>
              <div className="mt-4 flex items-center gap-2 text-xs font-black text-brand-700"><CheckCircle2 size={16} /> Fitur dijelaskan transparan sebelum memilih</div>
            </div>
          </div>
        </div>
      </section>

      {!isFree && (
        <section className="app-container pb-10">
          <div className="flex gap-4 rounded-2xl border border-accent-200 bg-accent-50 p-5 text-sm leading-6 text-accent-950">
            <ShieldCheck className="mt-0.5 shrink-0 text-accent-600" size={21} />
            <p><strong>Aktivasi setelah pembayaran.</strong> Nomor WhatsApp yang dipakai saat checkout menjadi identitas akun. Setelah pembayaran terverifikasi, <strong>#CSJW</strong>, website, dan dashboard menggunakan data usaha yang sama.</p>
          </div>
        </section>
      )}

      <section className="app-container py-16 sm:py-20">
        <div className="grid gap-10 rounded-[2.3rem] border border-slate-200 bg-white p-7 shadow-[0_26px_90px_rgba(26,63,42,.08)] lg:grid-cols-[.82fr_1.18fr] lg:p-11">
          <div>
            <span className="eyebrow">Nilai utamanya</span>
            <h2 className="section-title mt-5">{story.promiseTitle}</h2>
            <p className="mt-5 text-base leading-8 text-slate-500">{story.promiseText}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            {story.outcomes.map((outcome, index) => (
              <article key={outcome.label} className={`rounded-[1.7rem] border p-5 ${index === 1 ? "border-brand-700 bg-brand-700 text-white shadow-xl shadow-brand-900/10" : "border-slate-200 bg-slate-50"}`}>
                <p className={`text-3xl font-black tracking-[-.055em] ${index === 1 ? "text-white" : "text-slate-950"}`}>{outcome.value}</p>
                <p className={`mt-2 text-sm font-black ${index === 1 ? "text-brand-100" : "text-slate-800"}`}>{outcome.label}</p>
                <p className={`mt-3 text-xs leading-5 ${index === 1 ? "text-white/60" : "text-slate-500"}`}>{outcome.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="fitur" className="app-container py-20 sm:py-24">
        <div className="mx-auto max-w-3xl text-center">
          <span className="eyebrow">Fitur utama</span>
          <h2 className="section-title mt-5">Semua yang kamu perlukan untuk tahap ini.</h2>
          <p className="mt-4 text-base leading-8 text-slate-500">Bukan sekadar daftar fitur. Setiap bagian dirancang untuk membuat pengelolaan usaha lebih ringan dan pengalaman pembeli lebih meyakinkan.</p>
        </div>
        <div className="mt-12 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {plan.features.map(({ icon: PlanIcon, title, text }, index) => {
            const Icon = PlanIcon ?? featureIconFallbacks[index % featureIconFallbacks.length];
            return (
              <article key={title} className={`group rounded-[1.8rem] border p-6 transition duration-300 hover:-translate-y-2 hover:shadow-[0_28px_75px_rgba(30,70,45,.12)] ${index === 0 ? "border-emerald-950 bg-emerald-950 text-white" : "border-slate-200 bg-white"}`}>
                <span className={`grid h-12 w-12 place-items-center rounded-2xl ${index === 0 ? "bg-white/10 text-accent-300" : accent.icon}`}><Icon size={22} /></span>
                <h3 className="mt-5 text-lg font-black">{title}</h3>
                <p className={`mt-2 text-sm leading-6 ${index === 0 ? "text-white/60" : "text-slate-500"}`}>{text}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="bg-[#f0f6f1] py-20 sm:py-24">
        <div className="app-container">
          <div className="grid items-end gap-8 lg:grid-cols-[1fr_.8fr]">
            <div><span className="eyebrow">Dipakai sehari-hari</span><h2 className="section-title mt-5">{story.dailyTitle}</h2></div>
            <p className="text-base leading-8 text-slate-500">{story.dailyText}</p>
          </div>
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {story.dailyCards.map(({ icon: Icon, title, text }, index) => (
              <article key={title} className="group relative overflow-hidden rounded-[2rem] border border-white bg-white p-7 shadow-[0_20px_65px_rgba(30,65,43,.08)] transition duration-300 hover:-translate-y-2 hover:shadow-[0_30px_90px_rgba(30,65,43,.13)]">
                <span className="absolute right-5 top-4 text-7xl font-black tracking-[-.08em] text-slate-100">0{index + 1}</span>
                <span className={`relative grid h-14 w-14 place-items-center rounded-2xl ${accent.icon}`}><Icon size={25} /></span>
                <h3 className="relative mt-8 text-2xl font-black tracking-[-.035em] text-slate-950">{title}</h3>
                <p className="relative mt-3 text-sm leading-7 text-slate-500">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="app-container py-20 sm:py-24">
        <div className="grid gap-12 lg:grid-cols-[.7fr_1.3fr]">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <span className="eyebrow">Isi paket</span>
            <h2 className="section-title mt-5">Lebih jelas sebelum kamu memilih.</h2>
            <p className="mt-5 text-base leading-8 text-slate-500">Fitur dikelompokkan berdasarkan pekerjaan yang ingin diselesaikan, bukan istilah teknis yang sulit dipahami.</p>
          </div>
          <div className="space-y-5">
            {story.featureGroups.map((group, index) => (
              <article key={group.title} className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-7">
                <div className="grid gap-6 sm:grid-cols-[1fr_1.1fr]">
                  <div><p className="text-xs font-black uppercase tracking-[.17em] text-brand-700">Bagian {index + 1}</p><h3 className="mt-3 text-2xl font-black tracking-[-.04em] text-slate-950">{group.title}</h3><p className="mt-3 text-sm leading-7 text-slate-500">{group.text}</p></div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {group.items.map((item) => <div key={item} className="flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700"><span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-brand-100 text-brand-700"><Check size={15} strokeWidth={3} /></span>{item}</div>)}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={`bg-gradient-to-br ${accent.panel} py-20 text-white sm:py-24`}>
        <div className="app-container grid gap-12 lg:grid-cols-[.8fr_1.2fr]">
          <div><span className="inline-flex rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-black uppercase tracking-[.18em] text-brand-200">Cara mulai</span><h2 className="mt-6 text-4xl font-black leading-[1.02] tracking-[-.055em] sm:text-5xl">Dari pilih paket sampai siap digunakan.</h2><p className="mt-5 max-w-lg text-base leading-8 text-white/60">Setiap langkah dibuat jelas agar kamu tahu kapan website dibuat, kapan akun aktif, dan apa yang perlu dilengkapi.</p></div>
          <div className="space-y-3">
            {plan.steps.map((step, index) => <div key={step} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[.06] p-4 backdrop-blur-sm"><span className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl font-black ${index === plan.steps.length - 1 ? "bg-accent-500" : "bg-white/10"}`}>{index + 1}</span><p className="font-bold text-white/[.84]">{step}</p></div>)}
          </div>
        </div>
      </section>

      <section className="app-container py-20 sm:py-24">
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-[2rem] border border-brand-200 bg-brand-50 p-7 sm:p-9">
            <div className="flex items-center gap-2 text-brand-700"><Target size={19} /><p className="text-sm font-black uppercase tracking-[.14em]">Paket ini cocok untuk</p></div>
            <h2 className="mt-4 text-3xl font-black tracking-[-.045em] text-slate-950">Pilih berdasarkan kondisi usahamu sekarang.</h2>
            <div className="mt-7 grid gap-3">{plan.audience.map((item) => <div key={item} className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3.5 text-sm font-bold text-slate-700 shadow-sm"><CheckCircle2 size={18} className="shrink-0 text-brand-600" />{item}</div>)}</div>
          </div>
          <div className="rounded-[2rem] border border-accent-200 bg-accent-50 p-7 sm:p-9">
            <div className="flex items-center gap-2 text-accent-700"><CircleAlert size={19} /><p className="text-sm font-black uppercase tracking-[.14em]">Batas yang transparan</p></div>
            <h2 className="mt-4 text-3xl font-black tracking-[-.045em] text-slate-950">Tidak ada fitur penting yang disembunyikan.</h2>
            <div className="mt-7 grid gap-3">{plan.limitations.map((item) => <div key={item} className="flex items-start gap-3 rounded-2xl bg-white px-4 py-3.5 text-sm font-semibold leading-6 text-slate-600 shadow-sm"><CircleAlert size={17} className="mt-0.5 shrink-0 text-accent-600" />{item}</div>)}</div>
          </div>
        </div>
      </section>

      <section className="app-container py-16 sm:py-20">
        <div className="grid gap-10 lg:grid-cols-[.68fr_1.32fr]">
          <div><span className="eyebrow">Pertanyaan paket</span><h2 className="section-title mt-5">Yang perlu kamu tahu sebelum mulai.</h2><p className="mt-4 text-base leading-8 text-slate-500">Jawaban singkat tentang alur, batas, dan penggunaan paket {plan.name}.</p></div>
          <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white px-6 shadow-[0_20px_65px_rgba(28,63,41,.07)] sm:px-8">
            {story.faq.map(({ question, answer }) => (
              <details key={question} className="group border-b border-slate-200 py-5 last:border-b-0">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-base font-black text-slate-900 marker:hidden sm:text-lg"><span>{question}</span><span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-slate-200 text-brand-700 transition group-open:rotate-45 group-open:bg-brand-50"><ArrowRight size={17} /></span></summary>
                <p className="max-w-3xl pt-4 text-sm leading-7 text-slate-500">{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pb-10 sm:px-6">
        <div className={`mx-auto max-w-[1180px] overflow-hidden rounded-[2.5rem] bg-gradient-to-br ${accent.panel} p-8 text-white shadow-[0_40px_100px_rgba(10,55,32,.25)] sm:p-12`}>
          <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
            <div><p className="text-xs font-black uppercase tracking-[.2em] text-brand-200">{plan.name}</p><h2 className="mt-4 text-4xl font-black tracking-[-.055em]">Siap membawa usahamu ke tahap berikutnya?</h2><p className="mt-3 max-w-2xl text-white/60">{isFree ? "Mulai dari WhatsApp. Kirim data usaha secara bertahap dan website baru dibuat setelah semuanya lengkap." : "Daftarkan nomor WhatsApp, selesaikan pembayaran, lalu lanjutkan proses #CSJW dan pengelolaan melalui dashboard."}</p></div>
            {isFree ? (
              <a href={cta} target="_blank" rel="noopener noreferrer" className="btn-accent !px-6 !py-3.5"><MessageCircleMore size={18} /> {plan.primaryCta}</a>
            ) : (
              <Link to={cta} className="btn-accent !px-6 !py-3.5">Pilih {plan.name} <ArrowRight size={18} /></Link>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
